import { el, formatDate, type GoogleIdApi } from "../widgets/shared.js";

// The moderation stream, as a small same-origin app. The Google ID token goes
// in an Authorization header this page sets itself, never in a cookie, so
// nothing the browser attaches on its own can authenticate a request here.

type Status = "pending" | "approved" | "rejected";

interface Comment {
  id: number;
  slug: string;
  parent_author: string | null;
  author_name: string;
  author_email: string;
  body: string;
  status: Status;
  hearted: number;
  created_at: string;
}

const LABELS: Record<Status, string> = {
  approved: "Approved",
  pending: "Pending",
  rejected: "Rejected",
};

const ORDER: Status[] = ["approved", "pending", "rejected"];

/** Who is signed in, which is what a reply is signed as unless overridden. */
interface Moderator {
  email: string;
  name: string | null;
}

interface Settings {
  gravatar: boolean;
  preModerated: boolean;
  notify: boolean;
  /** Whether the Worker has a Discord webhook. Reported rather than stored: the
   *  switch below is unusable without one, and the page says why. */
  notifyConfigured: boolean;
}

const status = document.getElementById("status") as HTMLParagraphElement | null;
const signin = document.getElementById("signin") as HTMLDivElement | null;
const settings = document.getElementById("settings") as HTMLDivElement | null;
const queue = document.getElementById("queue") as HTMLDivElement | null;

let idToken = "";

// Where the token is kept across reloads. `auto_select` alone does not get a
// moderator back in without a click: One Tap only auto-selects with a single
// Google session and prior consent, and otherwise offers a card, which is no
// better than a button. sessionStorage rather than localStorage, so it dies
// with the tab.
//
// This is the one place Seopyo stores a credential, and it is a real
// concession — script on this page can read it, where a module variable could
// not. What makes it acceptable is the page it sits on: strict CSP, no
// `innerHTML` anywhere, no third-party script but Google's own.
const TOKEN_KEY = "seopyo.idToken";

/**
 * The `exp` claim, in seconds, or 0 if it can't be read. Not verification — the
 * Worker remains the only thing that decides whether a token is good; this only
 * avoids spending a round trip on one already visibly stale.
 */
function expiryOf(token: string): number {
  try {
    const segment = token.split(".")[1];
    if (!segment) return 0;
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const exp = (JSON.parse(atob(padded)) as { exp?: unknown }).exp;
    return typeof exp === "number" ? exp : 0;
  } catch {
    return 0;
  }
}

// Storage throws rather than no-ops when a browser has it disabled, so every
// path here degrades to the button.
function rememberToken(token: string): void {
  idToken = token;
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* no storage; the token still works for this page load */
  }
}

function forgetToken(): void {
  idToken = "";
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* nothing to forget */
  }
}

/** True if a still-valid token was restored, leaving nothing to sign in to. */
function restoreToken(): boolean {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return false;
  }
  if (!stored) return false;

  // A minute of headroom, so a token that expires mid-flight doesn't come back
  // as an error the moderator has to read.
  if (expiryOf(stored) * 1000 <= Date.now() + 60_000) {
    forgetToken();
    return false;
  }
  idToken = stored;
  return true;
}

function setStatus(text: string, isError = false): void {
  if (!status) return;
  status.textContent = text;
  status.className = isError ? "sub error" : "sub";
}

/**
 * Every request this page makes, carrying the moderator's token. Not every
 * response is JSON — the subscriber export is a file — so parsing lives one
 * level up and only the 401 is handled in common.
 */
async function request(
  path: string,
  method: "GET" | "POST" = "GET",
  body?: unknown,
): Promise<Response> {
  const response = await fetch(path, {
    method,
    headers: {
      Authorization: `Bearer ${idToken}`,
      ...(body === undefined
        ? {}
        : { "Content-Type": "application/json; charset=utf-8" }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 401) {
    // Drop the cached copy too, or the next reload retries it.
    forgetToken();
    throw new Error("Your session expired. Please sign in again.");
  }
  return response;
}

async function api<T>(
  path: string,
  method: "GET" | "POST" = "GET",
  body?: unknown,
): Promise<T> {
  const response = await request(path, method, body);

  const payload = (await response.json().catch(() => null)) as {
    error?: string;
  } | null;
  if (!response.ok) throw new Error(payload?.error ?? "Something went wrong.");
  return payload as T;
}

/**
 * One submission: all three statuses, always in the same place, with the
 * current one shaded. Nothing appears, disappears or moves on a click, so the
 * row cannot reflow under the pointer.
 */
function card(
  comment: Comment,
  moderator: Moderator,
  onChange: () => void,
  onReply: (reply: Comment) => void,
): HTMLElement {
  let current = comment.status;
  let currentBody = comment.body;
  let hearted = comment.hearted === 1;

  const result = el("span", { class: "note" });
  const buttons = new Map<Status, HTMLButtonElement>(
    ORDER.map((status): [Status, HTMLButtonElement] => [
      status,
      el("button", { class: status, type: "button" }, LABELS[status]),
    ]),
  );

  const text = el("div", { class: "body" }, currentBody);

  const editor = el("textarea", { class: "editor", rows: 6 });
  const save = el("button", { type: "button" }, "Save");
  const cancel = el("button", { type: "button" }, "Cancel");
  const edit = el(
    "button",
    { class: "edit", type: "button", "aria-pressed": "false" },
    "Edit",
  );
  const heart = el("button", { class: "heart", type: "button" }, "♥");
  const editing = el(
    "div",
    { class: "edit-pane", hidden: true },
    editor,
    el("div", { class: "edit-actions" }, save, cancel),
  );

  const replyName = el("input", {
    type: "text",
    maxlength: 64,
    autocomplete: "name",
    "aria-label": "Name",
  });
  const replyEmail = el("input", {
    type: "email",
    maxlength: 254,
    autocomplete: "email",
    "aria-label": "Email",
  });
  const replyBody = el("textarea", {
    class: "editor",
    rows: 4,
    "aria-label": "Reply",
  });
  const send = el("button", { type: "button" }, "Post reply");
  const dismiss = el("button", { type: "button" }, "Cancel");
  const reply = el(
    "button",
    { class: "reply", type: "button", "aria-pressed": "false" },
    "Reply",
  );
  const replying = el(
    "div",
    { class: "reply-pane", hidden: true },
    el("div", { class: "reply-who" }, replyName, replyEmail),
    replyBody,
    el("div", { class: "edit-actions" }, send, dismiss),
  );

  const controls = [
    ...buttons.values(),
    edit,
    heart,
    save,
    cancel,
    reply,
    send,
    dismiss,
  ];

  const article = el(
    "article",
    { class: `card ${current}` },
    el(
      "div",
      { class: "meta" },
      el("span", { class: "slug" }, comment.slug),
      el("span", {}, comment.author_name),
      comment.parent_author === null
        ? null
        : el("span", {}, `replying to ${comment.parent_author}`),
      el("span", {}, comment.author_email),
      el("span", {}, formatDate(comment.created_at)),
    ),
    text,
    editing,
    el(
      "div",
      { class: "actions" },
      el(
        "div",
        { class: "statuses", role: "group", "aria-label": "Status" },
        ...buttons.values(),
      ),
      edit,
      heart,
      reply,
      result,
    ),
    replying,
  );

  function paint(): void {
    article.className = `card ${current}`;
    heart.setAttribute("aria-pressed", String(hearted));
    heart.setAttribute(
      "aria-label",
      hearted ? "Remove your heart" : "Heart this comment",
    );
    // The pressed button is the only statement of where the comment stands, and
    // is what the shading hangs off in CSS.
    for (const [status, button] of buttons)
      button.setAttribute("aria-pressed", String(status === current));
  }

  function setBusy(busy: boolean, message = ""): void {
    for (const button of controls)
      if (busy) button.setAttribute("disabled", "");
      else button.removeAttribute("disabled");
    result.className = "note";
    result.textContent = message;
  }

  function fail(error: unknown): void {
    result.className = "note error";
    result.textContent = error instanceof Error ? error.message : "Failed.";
  }

  async function act(status: Status): Promise<void> {
    // The button for the current status is there to show state, not be pressed.
    if (status === current) return;

    setBusy(true, "Saving…");
    try {
      await api(`/admin/comments/${comment.id}/status`, "POST", { status });
      current = status;
      setBusy(false);
      paint();
      onChange();
    } catch (error) {
      setBusy(false);
      fail(error);
    } finally {
      buttons.get(current)?.focus();
    }
  }

  async function react(): Promise<void> {
    const wanted = !hearted;
    setBusy(true, "Saving…");
    try {
      await api(`/admin/comments/${comment.id}/heart`, "POST", {
        hearted: wanted,
      });
      hearted = wanted;
      setBusy(false);
      paint();
    } catch (error) {
      setBusy(false);
      fail(error);
    }
  }

  function openEditor(): void {
    editor.value = currentBody;
    text.hidden = true;
    editing.hidden = false;
    edit.setAttribute("aria-pressed", "true");
    result.textContent = "";
    editor.focus();
  }

  /** Closing discards whatever is in the textarea; the stored text is intact. */
  function closeEditor(): void {
    editing.hidden = true;
    text.hidden = false;
    edit.setAttribute("aria-pressed", "false");
    edit.focus();
  }

  async function commit(): Promise<void> {
    setBusy(true, "Saving…");
    try {
      const saved = await api<{ body: string }>(
        `/admin/comments/${comment.id}/body`,
        "POST",
        { body: editor.value },
      );
      currentBody = saved.body;
      text.textContent = currentBody;
      setBusy(false);
      closeEditor();
    } catch (error) {
      // The editor stays open on failure, so nothing typed is lost.
      setBusy(false);
      fail(error);
      editor.focus();
    }
  }

  /** Prefilled from the Google account each time it is empty, so a name
   *  typed for one reply doesn't follow the moderator to the next card. */
  function openReply(): void {
    replyName.value ||= moderator.name ?? "";
    replyEmail.value ||= moderator.email;
    replying.hidden = false;
    reply.setAttribute("aria-pressed", "true");
    result.textContent = "";
    replyBody.focus();
  }

  function closeReply(): void {
    replying.hidden = true;
    reply.setAttribute("aria-pressed", "false");
  }

  async function postReply(): Promise<void> {
    setBusy(true, "Posting…");
    try {
      const posted = await api<{ comment: Comment }>(
        `/admin/comments/${comment.id}/reply`,
        "POST",
        {
          author_name: replyName.value,
          author_email: replyEmail.value,
          body: replyBody.value,
        },
      );
      replyBody.value = "";
      setBusy(
        false,
        current === "approved"
          ? "Replied"
          : "Replied — hidden until this comment is approved",
      );
      closeReply();
      onReply(posted.comment);
    } catch (error) {
      setBusy(false);
      fail(error);
      replyBody.focus();
    }
  }

  for (const [status, button] of buttons)
    button.addEventListener("click", () => void act(status));
  reply.addEventListener("click", () =>
    replying.hidden ? openReply() : closeReply(),
  );
  dismiss.addEventListener("click", closeReply);
  send.addEventListener("click", () => void postReply());
  edit.addEventListener("click", () =>
    editing.hidden ? openEditor() : closeEditor(),
  );
  heart.addEventListener("click", () => void react());
  cancel.addEventListener("click", closeEditor);
  save.addEventListener("click", () => void commit());

  paint();
  return article;
}

/**
 * The mailing list, as a file. A button rather than a link, because
 * /admin/subscribers.csv needs the Authorization header and a link cannot carry
 * one without putting a credential in history and in access logs. So the CSV is
 * fetched and handed to a synthetic <a download>, whose object URL is revoked
 * on a timeout: revoking in the same task as the click can cancel the download
 * before the browser has read the blob.
 */
function exportPanel(): HTMLElement {
  const note = el("span", { class: "note" });
  const button = el("button", { type: "button" }, "Download subscribers (CSV)");

  async function download(): Promise<void> {
    button.setAttribute("disabled", "");
    note.className = "note";
    note.textContent = "Preparing…";

    let url: string | null = null;
    try {
      const response = await request("/admin/subscribers.csv");
      if (!response.ok) {
        // A failure here is JSON even though a success is not.
        const payload = (await response.json().catch(() => null)) as {
          error?: string;
        } | null;
        throw new Error(payload?.error ?? "Couldn't fetch the list.");
      }

      url = URL.createObjectURL(await response.blob());
      const link = el("a", { href: url, download: "subscribers.csv" });
      link.click();
      note.textContent = "Downloaded";
    } catch (error) {
      note.className = "note error";
      note.textContent = error instanceof Error ? error.message : "Failed.";
    } finally {
      if (url) {
        const pending = url;
        setTimeout(() => URL.revokeObjectURL(pending), 10_000);
      }
      button.removeAttribute("disabled");
    }
  }

  button.addEventListener("click", () => void download());

  return el(
    "div",
    { class: "tools" },
    button,
    note,
    el(
      "span",
      { class: "note" },
      "Import it into a mailing-list service to send anything.",
    ),
  );
}

/**
 * Site settings, as three checkboxes behind a collapsed summary. Each toggle
 * saves on change with no confirm step: holding comments for review is what a
 * moderator reaches for mid-spam-wave.
 */
function settingsPanel(
  initial: Settings,
  onSaved: (settings: Settings) => void,
): HTMLElement {
  let state = initial;
  const note = el("span", { class: "note" });

  const moderation = el("input", { type: "checkbox" });
  const gravatar = el("input", { type: "checkbox" });
  const notify = el("input", { type: "checkbox" });
  const inputs = [moderation, gravatar, notify];

  // The switch is on by default, so unconfigured it would otherwise read as
  // live while nothing was ever sent. Disabled and annotated instead: both are
  // secrets on the Worker, and nothing on this page could set them anyway.
  const missingTopic = el(
    "span",
    { class: "note", hidden: true },
    "— nowhere to send until DISCORD_WEBHOOK_URL is set on the Worker",
  );

  function paint(): void {
    moderation.checked = state.preModerated;
    gravatar.checked = state.gravatar;
    notify.checked = state.notify;
    // Re-applied on every paint, because save() re-enables all three.
    notify.disabled = !state.notifyConfigured;
    missingTopic.hidden = state.notifyConfigured;
  }

  async function save(): Promise<void> {
    const wanted = {
      preModerated: moderation.checked,
      gravatar: gravatar.checked,
      notify: notify.checked,
    };

    for (const input of inputs) input.disabled = true;
    note.className = "note";
    note.textContent = "Saving…";

    try {
      // Painted from what came back rather than from the click, so a checkbox
      // always shows what the database holds.
      state = await api<Settings>("/admin/settings", "POST", wanted);
      note.textContent = "Saved";
      onSaved(state);
    } catch (error) {
      note.className = "note error";
      note.textContent = error instanceof Error ? error.message : "Failed.";
    } finally {
      for (const input of inputs) input.disabled = false;
      paint();
    }
  }

  for (const input of inputs)
    input.addEventListener("change", () => void save());

  paint();

  return el(
    "details",
    { class: "settings" },
    el("summary", {}, "Settings"),
    el("label", {}, moderation, " Hold new comments for review"),
    el("label", {}, gravatar, " Show Gravatar avatars"),
    el("label", {}, notify, " Post new comments to Discord ", missingTopic),
    note,
  );
}

async function loadComments(): Promise<void> {
  if (!queue) return;
  setStatus("Loading…");

  try {
    const [{ comments, moderator, moderatorName }, stored] = await Promise.all([
      api<{
        comments: Comment[];
        moderator: string;
        moderatorName: string | null;
      }>("/admin/comments"),
      api<Settings>("/admin/settings"),
    ]);
    const identity = { email: moderator, name: moderatorName };

    let current = stored;

    // Counted from the cards rather than the payload, so the line stays true
    // as statuses change without another round trip.
    const report = () => {
      const waiting = queue.querySelectorAll(".card.pending").length;
      setStatus(
        [
          waiting === 0
            ? "Nothing awaiting review"
            : `${waiting} awaiting review`,
          current.preModerated
            ? "New comments are held for review"
            : "New comments post immediately",
          `Signed in as ${moderator}`,
        ].join(" · "),
      );
    };

    settings?.replaceChildren(
      settingsPanel(stored, (saved) => {
        current = saved;
        report();
      }),
      exportPanel(),
    );

    // Newest first, so a reply goes to the top of the stream.
    const show = (comment: Comment): HTMLElement =>
      card(comment, identity, report, (reply) => queue.prepend(show(reply)));

    queue.replaceChildren(
      ...(comments.length === 0
        ? [el("p", { class: "empty" }, "No comments yet.")]
        : comments.map(show)),
    );
    report();
  } catch (error) {
    setStatus(
      error instanceof Error ? error.message : "Couldn't load comments.",
      true,
    );
    if (!idToken) showSignIn();
  }
}

/** The page a Discord notification links to: one comment, its reply pane
 *  already open, and the moderator's replies collecting underneath. */
async function loadReplyPage(id: string): Promise<void> {
  if (!queue) return;
  setStatus("Loading…");

  try {
    const { comment, moderator, moderatorName } = await api<{
      comment: Comment;
      moderator: string;
      moderatorName: string | null;
    }>(`/admin/comments/${id}`);
    const identity = { email: moderator, name: moderatorName };

    settings?.replaceChildren(el("a", { href: "/" }, "← All comments"));
    const article = card(
      comment,
      identity,
      () => {},
      (reply) =>
        queue.append(
          card(
            reply,
            identity,
            () => {},
            () => {},
          ),
        ),
    );
    queue.replaceChildren(article);
    article.querySelector<HTMLButtonElement>(".reply")?.click();
    setStatus(`Signed in as ${moderator}`);
  } catch (error) {
    setStatus(
      error instanceof Error ? error.message : "Couldn't load the comment.",
      true,
    );
    if (!idToken) showSignIn();
  }
}

const replyTarget = location.pathname.match(/^\/reply\/(\d+)$/)?.[1];

function load(): Promise<void> {
  return replyTarget ? loadReplyPage(replyTarget) : loadComments();
}

/** GIS loads with `async`, so it may not be ready when this script runs. */
function whenGoogleReady(): Promise<GoogleIdApi> {
  return new Promise((resolve, reject) => {
    const deadline = Date.now() + 10000;
    const poll = () => {
      const api = window.google?.accounts?.id;
      if (api) return resolve(api);
      if (Date.now() > deadline)
        return reject(new Error("Google Sign-In failed to load."));
      setTimeout(poll, 100);
    };
    poll();
  });
}

// GIS warns when initialize() is called twice and only honours the last call,
// so it happens once and this holds the handle; showSignIn runs again whenever
// a token expires.
let googleId: GoogleIdApi | null = null;

/**
 * Shows the sign-in button, and asks Google to skip it if it can: with a single
 * Google session and prior consent, `auto_select` plus prompt() mints a token
 * with no interaction. Otherwise the cached token or the button covers it — see
 * TOKEN_KEY.
 */
async function showSignIn(): Promise<void> {
  if (!signin) return;
  const clientId = document.body.dataset.clientId ?? "";
  if (!clientId) return setStatus("GOOGLE_CLIENT_ID is not configured.", true);

  try {
    const google = googleId ?? (await whenGoogleReady());
    if (!googleId) {
      googleId = google;
      google.initialize({
        client_id: clientId,
        callback: (response) => {
          rememberToken(response.credential);
          signin.replaceChildren();
          void load();
        },
        auto_select: true,
        // A dismissal sets a `g_state` cooldown cookie that suppresses the
        // prompt on later loads, and clicking anywhere on the page would
        // otherwise count as one. The card still closes on its own X.
        cancel_on_tap_outside: false,
      });
    }
    google.renderButton(signin, {
      theme: "outline",
      size: "large",
      text: "signin_with",
    });
    google.prompt();
  } catch (error) {
    setStatus(
      error instanceof Error ? error.message : "Sign-in unavailable.",
      true,
    );
  }
}

// A stale cached token surfaces as the 401 in api(), which forgets it and
// leaves loadComments to fall through to the button.
if (restoreToken()) void load();
else void showSignIn();
